import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Eye, Plus, Send, Search, Monitor, Smartphone, X, Download, UserX, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../config';
import { exportCSV, exportPDF } from '../../utils/exportUtils';

const ACCOUNT_REASONS = [
  { value: 'tos_violation', label: { fr: 'Manquements aux CGU', en: 'TOS Violation' } },
  { value: 'fraud', label: { fr: 'Fraude', en: 'Fraud' } },
  { value: 'piracy', label: { fr: 'Piratage', en: 'Piracy' } },
  { value: 'multi_account', label: { fr: 'Multicompte', en: 'Multi-account' } },
  { value: 'other', label: { fr: 'Autre', en: 'Other' } },
];

export function AdminLicenses({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilterEmail, setAuditFilterEmail] = useState('');
  const [auditFilterFrom, setAuditFilterFrom] = useState('');
  const [auditFilterTo, setAuditFilterTo] = useState('');
  const [accountAction, setAccountAction] = useState(null); // { userId, email, name, type: 'deactivate'|'delete'|'purge' }

  useEffect(() => {
    axios.get(`${API}/admin/users-licenses`, { headers }).then(r => setUsers(r.data.users)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/admin/products`, { headers }).then(r => setProducts(r.data.products)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserDetail = async (userId) => {
    const r = await axios.get(`${API}/admin/user/${userId}/licenses`, { headers });
    setUserDetail(r.data);
    setSelectedUser(userId);
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (selectedUser && userDetail) {
    return <UserDetail data={userDetail} token={token} onBack={() => { setSelectedUser(null); setUserDetail(null); }} />;
  }

  return (
    <div className="space-y-4" data-testid="admin-licenses-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Rechercher un utilisateur..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="admin-user-search" />
        </div>
        <Button size="sm" variant="outline" onClick={() => { const d = filtered.map(u => ({
          Nom: u.name || '-', Email: u.email, Licences: u.entitlements_count,
          Appareils: u.activations_count, Rôles: u.roles?.join(', '),
        })); exportCSV(d, 'utilisateurs_licences'); }} className="border-border text-muted-foreground h-8" data-testid="export-users-csv">
          <Download className="w-3.5 h-3.5 mr-1" /> CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => { const d = filtered.map(u => ({
          Nom: u.name || '-', Email: u.email, Licences: u.entitlements_count,
          Appareils: u.activations_count, Rôles: u.roles?.join(', '),
        })); exportPDF(d, 'utilisateurs_licences', 'Utilisateurs et Licences'); }} className="border-border text-muted-foreground h-8" data-testid="export-users-pdf">
          <Download className="w-3.5 h-3.5 mr-1" /> PDF
        </Button>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-create-license-btn">
          <Plus className="w-4 h-4 mr-1" /> Créer licence
        </Button>
      </div>

      {loading ? <p className="text-muted-foreground text-sm">Chargement...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-licenses-table">
            <thead>
              <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b border-border">
                <th className="text-left py-2 pr-4">Utilisateur</th>
                <th className="text-left py-2 pr-4">Email</th>
                <th className="text-center py-2 pr-4">Licences</th>
                <th className="text-center py-2 pr-4">Appareils</th>
                <th className="text-left py-2 pr-4">Rôles</th>
                <th className="text-left py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className={`border-b border-border/50 last:border-0 hover:bg-white/[0.02] ${u.status === 'deactivated' ? 'opacity-50' : ''}`}>
                  <td className="py-2.5 pr-4 text-foreground">
                    {u.name || '-'}
                    {u.status === 'deactivated' && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">Désactivé</span>}
                  </td>
                  <td className="py-2.5 pr-4 text-foreground/80">{u.email}</td>
                  <td className="py-2.5 pr-4 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">{u.entitlements_count}</span></td>
                  <td className="py-2.5 pr-4 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{u.activations_count}</span></td>
                  <td className="py-2.5 pr-4"><span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{u.roles?.join(', ')}</span></td>
                  <td className="py-2.5">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-400 hover:text-blue-300" onClick={() => loadUserDetail(u.id)} data-testid={`view-user-${u.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                      </Button>
                      {!u.roles?.includes('admin') && u.status !== 'deactivated' && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-400 hover:text-amber-300" onClick={() => setAccountAction({ userId: u.id, email: u.email, name: u.name, type: 'deactivate' })} data-testid={`deactivate-user-${u.id}`}>
                          <UserX className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {!u.roles?.includes('admin') && u.status === 'deactivated' && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-400 hover:text-emerald-300" onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/users/${u.id}/reactivate`, {}, { headers });
                            toast.success('Compte réactivé');
                            const r = await axios.get(`${API}/admin/users-licenses`, { headers });
                            setUsers(r.data.users);
                          } catch { toast.error('Erreur'); }
                        }} data-testid={`reactivate-user-${u.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {!u.roles?.includes('admin') && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400 hover:text-red-300" onClick={() => setAccountAction({ userId: u.id, email: u.email, name: u.name, type: 'delete' })} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {!u.roles?.includes('admin') && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:text-red-500" onClick={() => setAccountAction({ userId: u.id, email: u.email, name: u.name, type: 'purge' })} data-testid={`purge-user-${u.id}`} title="Supprimer sans trace">
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit Trail */}
      <div className="mt-6 border-t border-border pt-4">
        <button onClick={() => {
          if (!showAudit) {
            axios.get(`${API}/admin/profile-audit`, { headers }).then(r => setAuditLogs(r.data.logs || [])).catch(() => {});
          }
          setShowAudit(!showAudit);
        }} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5" data-testid="toggle-audit-trail">
          <Shield className="w-3.5 h-3.5" />
          {showAudit ? 'Masquer' : 'Afficher'} l&apos;historique des modifications de profil
        </button>
        {showAudit && (
          <div className="mt-3" data-testid="audit-trail-list">
            <div className="flex flex-wrap gap-2 mb-3">
              <input value={auditFilterEmail} onChange={e => setAuditFilterEmail(e.target.value)}
                placeholder="Filtrer par email" className="px-3 py-1.5 rounded-lg bg-secondary/50 border border-border text-xs text-foreground w-48" data-testid="audit-filter-email" />
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground">Du</span>
                <input type="date" value={auditFilterFrom} onChange={e => setAuditFilterFrom(e.target.value)}
                  className="px-2 py-1.5 rounded-lg bg-secondary/50 border border-border text-xs text-foreground" data-testid="audit-filter-from" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground">Au</span>
                <input type="date" value={auditFilterTo} onChange={e => setAuditFilterTo(e.target.value)}
                  className="px-2 py-1.5 rounded-lg bg-secondary/50 border border-border text-xs text-foreground" data-testid="audit-filter-to" />
              </div>
              {(auditFilterEmail || auditFilterFrom || auditFilterTo) && (
                <button onClick={() => { setAuditFilterEmail(''); setAuditFilterFrom(''); setAuditFilterTo(''); }}
                  className="text-[10px] text-red-400 hover:text-red-300 px-2 py-1.5">Réinitialiser</button>
              )}
            </div>
            {(() => {
              const filtered = auditLogs.filter(log => {
                if (auditFilterEmail && !log.email?.toLowerCase().includes(auditFilterEmail.toLowerCase())) return false;
                if (auditFilterFrom && log.created_at && log.created_at.slice(0, 10) < auditFilterFrom) return false;
                if (auditFilterTo && log.created_at && log.created_at.slice(0, 10) > auditFilterTo) return false;
                return true;
              });
              return (
                <>
                  <p className="text-[10px] text-muted-foreground/60 mb-2">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</p>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {filtered.length === 0 && <p className="text-muted-foreground text-xs py-4 text-center">Aucune modification trouvée.</p>}
                    {filtered.map((log, i) => (
                      <div key={i} className="p-3 rounded-xl bg-card border border-border text-xs" data-testid={`audit-log-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-foreground font-medium">{log.email}</span>
                          <span className="text-muted-foreground/60">{log.created_at ? new Date(log.created_at).toLocaleString('fr-FR') : ''}</span>
                        </div>
                        <div className="space-y-0.5">
                          {(log.changes || []).map((c, j) => (
                            <div key={j} className="flex items-center gap-2 text-muted-foreground">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.field === 'password' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'}`}>
                                {c.field === 'password' ? 'Mot de passe' : 'Nom'}
                              </span>
                              {c.field !== 'password' && (
                                <>
                                  <span className="line-through text-muted-foreground/40">{c.old || '-'}</span>
                                  <span className="text-foreground/60">&rarr;</span>
                                  <span className="text-foreground">{c.new}</span>
                                </>
                              )}
                              {c.field === 'password' && <span className="text-muted-foreground/60">Modifié</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {showCreate && <CreateLicenseModal token={token} products={products} onClose={() => setShowCreate(false)} onCreated={() => {
        setShowCreate(false);
        axios.get(`${API}/admin/users-licenses`, { headers }).then(r => setUsers(r.data.users)).catch(() => {});
      }} />}

      {/* Account Action Modal (Deactivate / Delete) */}
      {accountAction && (
        <AccountActionModal
          action={accountAction}
          token={token}
          onClose={() => setAccountAction(null)}
          onDone={() => {
            setAccountAction(null);
            axios.get(`${API}/admin/users-licenses`, { headers }).then(r => setUsers(r.data.users)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

function UserDetail({ data: initialData, token, onBack }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [detailData, setDetailData] = useState(initialData);
  const { user, entitlements, activations, audit_logs, warnings_sent } = detailData;
  const [showWarning, setShowWarning] = useState(false);

  const reloadDetail = async () => {
    try {
      const r = await axios.get(`${API}/admin/user/${user.id}/licenses`, { headers });
      setDetailData(r.data);
    } catch {}
  };

  const handleReactivate = async (entId) => {
    if (!window.confirm('Réactiver cette licence ?')) return;
    try {
      await axios.post(`${API}/admin/licenses/reactivate`, { entitlement_id: entId }, { headers });
      toast.success('Licence réactivée');
      await reloadDetail();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleRevoke = async (entId) => {
    if (!window.confirm('Révoquer cette licence ?')) return;
    try {
      await axios.post(`${API}/admin/licenses/revoke`, { entitlement_id: entId }, { headers });
      toast.success('Licence révoquée');
      await reloadDetail();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleResetActivation = async (actId) => {
    if (!window.confirm('Désactiver cet appareil ?')) return;
    try {
      await axios.post(`${API}/admin/licenses/reset`, { activation_id: actId }, { headers });
      toast.success('Appareil désactivé');
      await reloadDetail();
    } catch (e) { toast.error('Erreur'); }
  };

  const handlePurgeEntitlement = async (entId) => {
    if (!window.confirm('SUPPRIMER DEFINITIVEMENT cette licence et toutes ses activations ? Aucune trace ne sera conservée.')) return;
    try {
      await axios.delete(`${API}/admin/entitlements/${entId}/purge`, { headers });
      toast.success('Licence supprimée définitivement');
      await reloadDetail();
    } catch (e) { toast.error('Erreur'); }
  };

  const handlePurgeActivation = async (actId) => {
    if (!window.confirm('SUPPRIMER DEFINITIVEMENT cet appareil ? Aucune trace ne sera conservée.')) return;
    try {
      await axios.delete(`${API}/admin/activations/${actId}/purge`, { headers });
      toast.success('Appareil supprimé définitivement');
      await reloadDetail();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="user-detail-panel">
      <button onClick={onBack} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mb-4" data-testid="user-detail-back">
        &larr; Retour à la liste
      </button>
      <div className="p-4 rounded-xl bg-secondary/50 border border-border/50">
        <h3 className="text-foreground font-medium mb-1">{user.name || user.email}</h3>
        <p className="text-muted-foreground text-sm">{user.email}</p>
        <p className="text-xs text-muted-foreground mt-1">Rôles : {user.roles?.join(', ')} | Inscrit : {new Date(user.created_at).toLocaleDateString('fr-FR')}</p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" className="text-amber-400 border-amber-400/30 hover:bg-amber-500/10" onClick={() => setShowWarning(true)} data-testid="send-warning-btn">
            <Send className="w-3.5 h-3.5 mr-1" /> Avertissement
          </Button>
        </div>
      </div>

      <h4 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mt-4">Licences ({entitlements.length})</h4>
      {entitlements.map(ent => (
        <div key={ent.id} className="p-3 rounded-lg bg-background border border-border/50" data-testid={`admin-ent-${ent.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-foreground text-sm font-medium">{ent.product_slug || ent.product_family || 'N/A'}</span>
              <span className="ml-2 text-xs text-muted-foreground">{ent.edition} | {(ent.platforms || []).join(', ')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${ent.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{ent.status}</span>
              {ent.status === 'active' && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-red-400 hover:text-red-300 text-xs" onClick={() => handleRevoke(ent.id)} data-testid={`revoke-${ent.id}`}>
                  Révoquer
                </Button>
              )}
              {ent.status === 'revoked' && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-emerald-400 hover:text-emerald-300 text-xs" onClick={() => handleReactivate(ent.id)} data-testid={`reactivate-${ent.id}`}>
                  Réactiver
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-6 px-2 text-red-600 hover:text-red-500 text-xs" onClick={() => handlePurgeEntitlement(ent.id)} data-testid={`purge-ent-${ent.id}`}>
                Supprimer
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Limite : {ent.device_limit || ent.max_activations || 1} | Canal : {ent.channel || 'N/A'} | ID : {ent.id}</p>
          {activations.filter(a => a.entitlement_id === ent.id).map(act => (
            <div key={act.id} className="mt-2 ml-4 p-2 rounded bg-secondary/30 flex items-center justify-between" data-testid={`admin-act-${act.id}`}>
              <div className="flex items-center gap-2">
                {act.platform === 'mobile' ? <Smartphone className="w-3.5 h-3.5 text-muted-foreground" /> : <Monitor className="w-3.5 h-3.5 text-muted-foreground" />}
                <span className="text-xs text-foreground">{act.device_name || 'Inconnu'}</span>
                <span className="text-xs text-muted-foreground">{act.device_type} | {act.device_fingerprint?.slice(0, 20)}...</span>
              </div>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-red-400 hover:text-red-300 text-xs" onClick={() => handleResetActivation(act.id)} data-testid={`reset-act-${act.id}`}>
                Désactiver
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-red-600 hover:text-red-500 text-xs" onClick={() => handlePurgeActivation(act.id)} data-testid={`purge-act-${act.id}`}>
                Supprimer
              </Button>
            </div>
          ))}
        </div>
      ))}

      {warnings_sent?.length > 0 && (
        <>
          <h4 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mt-4">Avertissements envoyés ({warnings_sent.length})</h4>
          {warnings_sent.map(w => (
            <div key={w.id} className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs">
              <p className="text-amber-400">{w.subject}</p>
              <p className="text-muted-foreground">{new Date(w.sent_at).toLocaleString('fr-FR')} | {w.sent ? 'Envoyé' : 'Échec'}</p>
            </div>
          ))}
        </>
      )}

      {audit_logs?.length > 0 && (
        <>
          <h4 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mt-4">Historique ({audit_logs.length})</h4>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {audit_logs.slice(0, 20).map(log => (
              <div key={log.id} className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="text-foreground/60">{new Date(log.created_at).toLocaleString('fr-FR')}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${log.event.includes('deactivat') || log.event.includes('revok') ? 'bg-red-500/10 text-red-400' : 'bg-blue-500/10 text-blue-400'}`}>{log.event}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {showWarning && <WarningModal token={token} userId={user.id} userName={user.name || user.email} userEmail={user.email} onClose={() => setShowWarning(false)} />}
    </div>
  );
}

function CreateLicenseModal({ token, products, onClose, onCreated }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [email, setEmail] = useState('');
  const [productSlug, setProductSlug] = useState('hexopdf-standard-desktop');
  const [deviceLimit, setDeviceLimit] = useState(1);
  const [sending, setSending] = useState(false);
  const selectedProduct = products.find(p => p.slug === productSlug);

  const handleCreate = async () => {
    if (!email) return toast.error('Email requis');
    setSending(true);
    try {
      await axios.post(`${API}/admin/create-license`, {
        user_email: email, product_slug: productSlug,
        edition: selectedProduct?.edition || 'standard',
        platforms: selectedProduct?.platforms || ['desktop'],
        channel: 'admin', device_limit: deviceLimit,
        features: selectedProduct?.features || ['pdf_edit', 'merge', 'split', 'annotate', 'ocr_basic'],
      }, { headers });
      toast.success('Licence créée');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="create-license-modal">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground font-medium">Créer une licence</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Email utilisateur</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com"
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="create-license-email" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Produit</label>
            <select value={productSlug} onChange={e => setProductSlug(e.target.value)}
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="create-license-product">
              {products.filter(p => p.status === 'active').map(p => (
                <option key={p.slug} value={p.slug}>{p.name} ({p.edition})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Limite d'appareils</label>
            <input type="number" min={1} max={10} value={deviceLimit} onChange={e => setDeviceLimit(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="create-license-limit" />
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} disabled={sending} data-testid="create-license-submit">
            {sending ? 'Création...' : 'Créer la licence'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function WarningModal({ token, userId, userName, userEmail, onClose }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [templates, setTemplates] = useState(null);
  const [template, setTemplate] = useState('suspicious_activity');
  const [lang, setLang] = useState('fr');
  const [customDetails, setCustomDetails] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/warning-templates`, { headers }).then(r => {
      setTemplates(r.data.templates);
      applyTemplate(r.data.templates, 'suspicious_activity', 'fr');
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = (tpls, tplKey, lng) => {
    const t = tpls?.[tplKey]?.[lng];
    if (t) {
      setEditSubject(t.subject);
      setEditBody(t.body
        .replace('{name}', userName)
        .replace('{email}', userEmail)
        .replace('{details}', customDetails || 'Détails non spécifiés.')
      );
    }
  };

  useEffect(() => {
    if (templates) applyTemplate(templates, template, lang);
  }, [template, lang, customDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const templateNames = {
    suspicious_activity: 'Activité suspecte',
    license_violation: 'Violation de licence',
    account_suspension: 'Suspension de compte',
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/send-warning`, {
        user_id: userId, template, language: lang,
        custom_details: customDetails,
        custom_subject: editSubject,
        custom_body: editBody,
      }, { headers });
      toast.success(r.data.sent ? 'Email envoyé' : 'Email enregistré (envoi échoué)');
      onClose();
    } catch (e) { toast.error('Erreur'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="warning-modal">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-2xl mx-4 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground font-medium">Avertissement - {userName}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Modèle</label>
              <select value={template} onChange={e => setTemplate(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="warning-template-select">
                {Object.entries(templateNames).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Langue</label>
              <select value={lang} onChange={e => setLang(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground">
                <option value="fr">FR</option>
                <option value="en">EN</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Détails insérés</label>
            <textarea value={customDetails} onChange={e => setCustomDetails(e.target.value)} rows={2} placeholder="Détaillez la raison..."
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground resize-none" data-testid="warning-details" />
          </div>

          {/* Prévisualisation complète du message */}
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 overflow-hidden">
            <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20">
              <p className="text-[10px] uppercase tracking-wider text-amber-400/80">Prévisualisation du message</p>
            </div>
            <div className="p-4 space-y-2">
              <div>
                <label className="text-[10px] text-muted-foreground">Sujet</label>
                <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                  className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground mt-0.5" data-testid="warning-subject-edit" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Corps du message</label>
                <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={10}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs text-foreground resize-none mt-0.5 leading-relaxed font-mono" data-testid="warning-body-edit" />
              </div>
            </div>
          </div>

          <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={handleSend} disabled={sending} data-testid="warning-send-btn">
            {sending ? 'Envoi...' : "Envoyer l'avertissement"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function AccountActionModal({ action, token, onClose, onDone }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [reason, setReason] = useState('tos_violation');
  const [language, setLanguage] = useState('fr');
  const [details, setDetails] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);
  const isDelete = action.type === 'delete';
  const isPurge = action.type === 'purge';
  const expectedConfirm = isPurge ? 'PURGER' : isDelete ? 'SUPPRIMER' : 'DESACTIVER';

  const handleConfirm = async () => {
    if (confirmText !== expectedConfirm) return;
    setProcessing(true);
    try {
      if (isPurge) {
        await axios.delete(`${API}/admin/users/${action.userId}/purge`, { headers });
        toast.success(`Compte ${action.email} purgé définitivement`);
      } else if (isDelete) {
        const body = { user_id: action.userId, reason, language, custom_details: details };
        await axios.delete(`${API}/admin/users/${action.userId}`, { headers, data: body });
        toast.success(`Compte ${action.email} supprimé`);
      } else {
        const body = { user_id: action.userId, reason, language, custom_details: details };
        await axios.post(`${API}/admin/users/${action.userId}/deactivate`, body, { headers });
        toast.success(`Compte ${action.email} désactivé`);
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="account-action-modal">
      <div className="bg-card border border-border rounded-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPurge ? 'bg-red-600/20' : isDelete ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
            {isPurge ? <AlertTriangle className="w-5 h-5 text-red-500" /> : isDelete ? <Trash2 className="w-5 h-5 text-red-400" /> : <UserX className="w-5 h-5 text-amber-400" />}
          </div>
          <div>
            <h3 className="text-foreground font-medium text-sm">{isPurge ? 'Purger le compte (sans trace)' : isDelete ? 'Supprimer le compte' : 'Désactiver le compte'}</h3>
            <p className="text-muted-foreground text-xs">{action.email}</p>
          </div>
          <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {(isDelete || isPurge) && (
          <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 mb-4">
            <p className="text-red-400 text-xs flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />
              {isPurge ? 'PURGE TOTALE : toutes les licences, activations, paiements, logs et données seront supprimés sans aucune trace.' : 'Cette action est irréversible. Toutes les licences et données seront supprimées.'}
            </p>
          </div>
        )}

        <div className="space-y-3">
          {!isPurge && (
          <>
          <div>
            <label className="text-xs text-muted-foreground">Motif</label>
            <select value={reason} onChange={e => setReason(e.target.value)}
              className="w-full mt-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="action-reason-select">
              {ACCOUNT_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label.fr}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Langue de l'email</label>
            <div className="flex gap-2 mt-1">
              {['fr', 'en'].map(l => (
                <button key={l} onClick={() => setLanguage(l)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${language === l ? 'bg-blue-600 text-white' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
                  data-testid={`action-lang-${l}`}>
                  {l === 'fr' ? 'Français' : 'English'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Détails supplémentaires (optionnel)</label>
            <textarea value={details} onChange={e => setDetails(e.target.value)}
              placeholder="Précisions sur le motif..."
              rows={2} className="w-full mt-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground resize-none" data-testid="action-details" />
          </div>
          </>
          )}

          <div>
            <label className="text-xs text-muted-foreground">
              Tapez <span className="font-mono font-bold text-foreground">{expectedConfirm}</span> pour confirmer
            </label>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
              className="w-full mt-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono"
              placeholder={expectedConfirm} data-testid="action-confirm-input" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" className="flex-1 border-border text-muted-foreground" onClick={onClose}>Annuler</Button>
          <Button
            className={`flex-1 text-white ${isDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
            disabled={confirmText !== expectedConfirm || processing}
            onClick={handleConfirm}
            data-testid="action-confirm-btn">
            {processing ? 'Traitement...' : isPurge ? 'Purger définitivement' : isDelete ? 'Supprimer définitivement' : 'Désactiver le compte'}
          </Button>
        </div>
      </div>
    </div>
  );
}
